import React, {useState} from 'react'
import {LogbogNotatApi} from "../../../../services/api/logbogNotat/logbogNotatApi"
import {HttpClientService} from '../../../../services/httpClient/httpClientService';
import {ValidationForm} from '../../../components/validation/components/validationForm';
import {ValidationFormSubmitButton} from '../../../components/validation/components/validationFormButton';
import {Localizer} from '../../../../infrastructure/localization/localizer';
import {setSlideinState, SlideInStateEnum} from '../../../components/slideIn/slideInSlice';
import {HandleError} from '../../../infrastructure/errors/errorBoundary';
import {useAppDispatch, useAppSelector} from '../../../../app/hooks';
import {NotatType} from 'core/sharedmodels/notat/notatType';
import {LaegekompetenceMeta} from 'core/sharedmodels/kompetence/laegekompetenceMeta';
import {
    clearSelectedLaegekompetenceMetas,
    kompetenceCardReducer,
    KompetenceCardState,
    setActiveKompetencePageTab,
    setKompetenceCardStateShowCheckboxes} from 'pages/kompetencerPage/cards/kompetenceCardSlice';
import {ReactFileUploaderFile} from 'core/components/fileUpload/reactFileUploader';
import {BasicNotatCrudFields} from "../basicNotatCrudFields";
import {FileMetadata} from 'core/sharedmodels/fileMetaData/fileMetaData';
import {
    BuildNewCrudNotatModel,
    crudNotatValidationSchema,
    NotatCrudModel
} from "../../../sharedmodels/notat/notatCrudModel";
import {AccountService} from "../../../../services/account/accountService";
import Tooltip from 'core/components/tooltips/Tooltip';

const notatApi = new LogbogNotatApi(new HttpClientService(), new AccountService())

export interface CreateLaegekompetenceNotatProps {
    modalId: string;
    laegekompetencer: LaegekompetenceMeta[];
    notatType: NotatType;
    titel?: string;
}

export function CreateLaegekompetenceNotat(props: CreateLaegekompetenceNotatProps) {
    const dispatch = useAppDispatch();

    const [createNotatFailed, setCreateNotatFailed] = useState(false);
    const [uploadedFilesState, setUploadedFilesState] = useState<ReactFileUploaderFile[]>([]);
    const kompetenceCardState = useAppSelector(kompetenceCardReducer) as KompetenceCardState;

    const createLaegekompetenceNotat = async (inputModel: NotatCrudModel) => {
        dispatch(setSlideinState({ state: SlideInStateEnum.ActionOngoing, slideInId: props.modalId }))
        const newNotat = await notatApi.createNotat(inputModel, uploadedFilesState.map(tf => tf.fileMetadata.id));

        if (newNotat) {
            setCreateNotatFailed(false);
            dispatch(setSlideinState({ state: SlideInStateEnum.Closing, slideInId: props.modalId }))
            dispatch(clearSelectedLaegekompetenceMetas())
            dispatch(setKompetenceCardStateShowCheckboxes([]))
            dispatch(setActiveKompetencePageTab(Localizer.global_notes()))
        }
    }

    const fileUploadedCallback = async (uploadedFiles: ReactFileUploaderFile[]) => {
        setUploadedFilesState(arr => [...arr, ...uploadedFiles]);
    }

    const fileDeletedCallback = (fileMetaData: FileMetadata) => {
        let updatedFileUploadedState = [...uploadedFilesState];
        let deleteIndex = updatedFileUploadedState.findIndex(file => file.fileMetadata.id === fileMetaData.id);

        updatedFileUploadedState.splice(deleteIndex, 1);
        setUploadedFilesState(updatedFileUploadedState);
    }

    const render = (
        <ValidationForm
            initialValues={BuildNewCrudNotatModel(
                props.laegekompetencer.map(x => x.laegekompetenceId),
                props.notatType,
                kompetenceCardState.activeLaegekompetenceMeta.UserId,
                props.titel)
            }
            enableReinitialize
            validationSchema={crudNotatValidationSchema}
            onSubmit={async (values) => {
                await createLaegekompetenceNotat(values)
                    .catch(err => {
                        HandleError(err);
                        setCreateNotatFailed(true);
                        dispatch(setSlideinState({ state: SlideInStateEnum.Opened, slideInId: props.modalId }));
                    });
            }}>
            {(formik) => (
                <>
                    {props.laegekompetencer &&
                    <div className="row mb-2">
                        <div className="col-sm-12">
                            {props.laegekompetencer.length > 1 &&
                            <h6>{Localizer.global_tilknyttedeKompetencer()}</h6>
                            }
                            {props.laegekompetencer.length === 1 &&
                            <h6>{Localizer.global_tilknyttedeKompetence()}</h6>
                            }
                            {props.laegekompetencer.map((laegekompetence, i) => (
                                <React.Fragment key={laegekompetence.kompetenceId + i}>
                                     <Tooltip
                                        className={"col-sm-12 text-truncate badge badge-pill badge-primary margin-right-xs"}
                                        title={laegekompetence.titel}
                                    >
                                        {laegekompetence.titel}
                                    </Tooltip>
                                </React.Fragment>
                            ))}
                        </div>
                    </div>}

                    <BasicNotatCrudFields modalId={props.modalId} formik={formik}
                                          notatType={props.notatType}
                                          notatUserId={kompetenceCardState.activeLaegekompetenceMeta.UserId}
                                          fileUploadedCallbackOverride={fileUploadedCallback}
                                          fileDeletedCallbackOverride={fileDeletedCallback} />

                    <div className="modal-actions">
                        {createNotatFailed &&
                        <div className="form-group margin-bottom-m">
                            <div className="field-validation-error">{Localizer.notatPage_notatCreateFailed()}</div>
                        </div>
                        }

                        <ValidationFormSubmitButton formikProps={formik}>
                            {Localizer.notatPage_createNotat()}
                        </ValidationFormSubmitButton>
                    </div>
                </>
            )}
        </ValidationForm>
    )

    return <>{render}</>
}
